import React from "react";

/**
 * Returns columns for Appraisal Progress list (DataTable component).
 * Plain factory — do not use hooks here (called during parent render).
 */
const Columns = ({
  getEmployeeName,
  getAppraisalFormName,
  getAppraiserName,
  getDepartmentName,
  getCampaignName,
  getDesignationName,
  formatDate,
  /** Plain-text status label for search/sort/cell accessor */
  getStatusLabel,
  getStatus,
  getActions,
}) => [
  {
    id: "sr_no",
    header: "SR No.",
    accessor: null,
    cell: (_, __, rowIndex, sequentialRowNumber) =>
      sequentialRowNumber || (rowIndex !== undefined ? rowIndex + 1 : ""),
    sortable: false,
    searchable: false,
  },
  {
    id: "appraisal_form_name",
    header: "Appraisal Form Name",
    accessor: (row) => getAppraisalFormName(row),
    sortable: true,
    searchable: true,
  },
  {
    id: "appraiser_name",
    header: "Appraiser Name",
    accessor: (row) => getAppraiserName(row),
    sortable: true,
    searchable: true,
  },
  {
    id: "name",
    header: "Employee Name",
    accessor: (row) =>
      getEmployeeName(row.employee || row.employee_name || row.employeeName),
    sortable: true,
    searchable: true,
  },
  {
    id: "department",
    header: "Department",
    accessor: (row) =>
      getDepartmentName(
        row.employee?.department || row.department || row.department_name
      ),
    sortable: true,
    searchable: true,
  },
  {
    id: "campaign",
    header: "Campaign",
    accessor: (row) => {
      const campaign =
        row.employee?.campaign ||
        row.employee?.campaigns ||
        row.campaign ||
        row.campaigns ||
        row.appraisal_setup?.campaigns;
      return getCampaignName(campaign);
    },
    sortable: true,
    searchable: true,
  },
  {
    id: "month",
    header: "Month",
    accessor: (row) => {
      const MONTHS_ORDER = [
        "january",
        "february",
        "march",
        "april",
        "may",
        "june",
        "july",
        "august",
        "september",
        "october",
        "november",
        "december",
      ];
      const capitalize = (s) =>
        s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;

      if (Array.isArray(row.months) && row.months.length > 0) {
        const normalized = row.months
          .map((m) => String(m).trim().toLowerCase())
          .filter(Boolean)
          .sort((a, b) => MONTHS_ORDER.indexOf(a) - MONTHS_ORDER.indexOf(b))
          .map(capitalize);
        return normalized.join(", ");
      }

      if (typeof row.month === "string" && row.month.includes(",")) {
        const normalized = row.month
          .split(",")
          .map((m) => String(m).trim().toLowerCase())
          .filter(Boolean)
          .sort((a, b) => MONTHS_ORDER.indexOf(a) - MONTHS_ORDER.indexOf(b))
          .map(capitalize);
        return normalized.join(", ");
      }

      const monthValue = row.month || row.appraisal_month || row.month_name;

      if (monthValue) {
        return capitalize(String(monthValue).trim());
      }

      const dateValue = row.appraisal_date_time || row.date || row.created_at;
      if (!dateValue) return "";

      const parsedDate = new Date(dateValue);
      if (Number.isNaN(parsedDate.getTime())) return "";

      return parsedDate.toLocaleString("default", { month: "long" });
    },
    sortable: true,
    searchable: true,
  },
  {
    id: "quarter",
    header: "Quarter",
    accessor: (row) => {
      const quarterByMonth = {
        january: "Q1",
        february: "Q1",
        march: "Q1",
        april: "Q2",
        may: "Q2",
        june: "Q2",
        july: "Q3",
        august: "Q3",
        september: "Q3",
        october: "Q4",
        november: "Q4",
        december: "Q4",
      };

      const monthValues =
        Array.isArray(row.months) && row.months.length > 0
          ? row.months
          : row.month
            ? String(row.month).split(",")
            : [];

      const detectedQuarters = [
        ...new Set(
          monthValues
            .map((m) => quarterByMonth[String(m).trim().toLowerCase()])
            .filter(Boolean)
        ),
      ];

      if (detectedQuarters.length > 0) {
        return detectedQuarters.join(", ");
      }

      const dateValue = row.appraisal_date_time || row.date || row.created_at;
      if (!dateValue) return "";
      const parsedDate = new Date(dateValue);
      if (Number.isNaN(parsedDate.getTime())) return "";
      return `Q${Math.floor(parsedDate.getMonth() / 3) + 1}`;
    },
    sortable: true,
    searchable: true,
  },
  {
    id: "date",
    header: "Date",
    accessor: (row) =>
      formatDate(row.appraisal_date_time || row.date || row.created_at),
    sortable: true,
    searchable: true,
  },
  {
    id: "status",
    header: "Status",
    accessor: (row) =>
      getStatusLabel(
        row.status ??
          row.appraisal_status ??
          (row.is_deleted === "y" ? "Deleted" : "Active")
      ),
    sortable: true,
    searchable: true,
    cell: (_, row) =>
      getStatus(
        row.status ??
          row.appraisal_status ??
          (row.is_deleted === "y" ? "Deleted" : "Active")
      ),
  },
  {
    id: "actions",
    header: "Actions",
    accessor: null,
    cell: (_, row) => getActions(row),
    sortable: false,
    searchable: false,
  },
];

export default Columns;
